/**
 * TenantReadOnlyBanner (QM8D Rev.1000 · FIX-02).
 *
 * Globales Top-Banner, das User informiert, dass der aktuelle Tenant in
 * Read-Only-Mode ist (DR-Failover oder geplante Wartung). Wird vom
 * Axios-Response-Interceptor gespeist (``X-Tenant-ReadOnly: true``).
 *
 * UX:
 * - Sticky Top-Banner mit Warn-Farben (Amber).
 * - ``aria-live="polite"`` fuer Screen-Reader.
 * - Schliessbar via ``Dismiss``-Button — verschwindet aber automatisch
 *   wieder, sobald ein neuer Read-Only-Header eintrifft.
 * - Lokalisiert in 10 Sprachen (i18n key ``tenant.readOnlyBanner.*``).
 */
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, ServerCog } from 'lucide-react';
import { useTenantReadOnly } from '../../contexts/TenantReadOnlyContext';
import { useLanguage } from '../../contexts/LanguageContext';

export default function TenantReadOnlyBanner() {
  const { t } = useLanguage();
  const { readOnly, lastSeenAt } = useTenantReadOnly();
  const [dismissed, setDismissed] = useState(false);

  // Reset Dismiss-Zustand, wenn ein neuer Read-Only-Header reinkommt
  useEffect(() => {
    if (readOnly && lastSeenAt) {
      setDismissed(false);
    }
  }, [readOnly, lastSeenAt]);

  if (!readOnly || dismissed) {
    return null;
  }

  const tt = (k, fb) => t(k) || fb;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="tenant-readonly-banner"
      className="sticky top-0 z-40 w-full border-b border-amber-300 bg-amber-50/95 backdrop-blur-sm"
    >
      <div className="flex items-start gap-3 px-4 py-3 sm:px-6">
        <div className="flex-shrink-0 mt-0.5">
          <AlertTriangle className="h-5 w-5 text-amber-700" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-semibold text-amber-900"
            data-testid="tenant-readonly-banner-title"
          >
            <ServerCog className="inline h-4 w-4 mr-1.5" aria-hidden="true" />
            {tt('tenant.readOnlyBanner.title', 'Read-Only-Modus aktiv')}
          </p>
          <p
            className="text-xs sm:text-sm text-amber-800 mt-0.5"
            data-testid="tenant-readonly-banner-desc"
          >
            {tt(
              'tenant.readOnlyBanner.desc',
              'Der Tenant befindet sich aktuell im Read-Only-Modus (DR-Failover oder Wartung). Schreib-Operationen sind temporär deaktiviert. Bitte versuchen Sie es in wenigen Minuten erneut.',
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label={tt('common.close', 'Schließen')}
          data-testid="tenant-readonly-banner-dismiss"
          className="flex-shrink-0 rounded-md p-1 text-amber-700 hover:bg-amber-100 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
